import type { Trainer } from 'src/models/trainer/_types';
import type { Identifier } from 'src/types/utilityTypes';

type MinimalPlatform = {
  id: Identifier | string | number;
  fbmPlatformNodeId?: Identifier | string | number;
};

type MinimalNode = {
  id: Identifier | string | number;
  parentId?: Identifier | string | number | null;
};

export function getHqAssignedDescendantPlatformIds(
  trainer: Trainer | undefined,
  platforms: MinimalPlatform[],
  nodes: MinimalNode[]
): string[] {
  if (!trainer) return [];
  const { hqAssignmentType, hqAssignmentId } = trainer;
  if (!hqAssignmentType || hqAssignmentId == null) return [];

  if (hqAssignmentType === 'platform') {
    return [String(hqAssignmentId)];
  }

  if (hqAssignmentType === 'platform_node') {
    const nodeId = String(hqAssignmentId);
    const childrenByParent: Record<string, string[]> = {};
    (nodes || []).forEach((n) => {
      const key = n.parentId != null ? String(n.parentId) : 'root';
      if (!childrenByParent[key]) childrenByParent[key] = [];
      childrenByParent[key].push(String(n.id));
    });

    const collectDescendants = (startId: string): Set<string> => {
      const result = new Set<string>();
      const stack = [startId];
      while (stack.length) {
        const current = stack.pop() as string;
        if (!result.has(current)) {
          result.add(current);
          const kids = childrenByParent[current] || [];
          kids.forEach((kid) => stack.push(kid));
        }
      }
      return result;
    };

    const allowedNodeIds = collectDescendants(nodeId);
    return (platforms || [])
      .filter((p) => allowedNodeIds.has(String(p.fbmPlatformNodeId)))
      .map((p) => String(p.id));
  }

  return [];
}

export function getHqAssignedDescendantNodeIds(
  trainer: Trainer | undefined,
  nodes: MinimalNode[]
): string[] {
  if (!trainer) return [];
  const { hqAssignmentType, hqAssignmentId } = trainer;
  if (hqAssignmentType !== 'platform_node' || hqAssignmentId == null) return [];

  const nodeId = String(hqAssignmentId);
  const childrenByParent: Record<string, string[]> = {};
  (nodes || []).forEach((n) => {
    const key = n.parentId != null ? String(n.parentId) : 'root';
    if (!childrenByParent[key]) childrenByParent[key] = [];
    childrenByParent[key].push(String(n.id));
  });

  const collectDescendants = (startId: string): Set<string> => {
    const result = new Set<string>();
    const stack = [startId];
    while (stack.length) {
      const current = stack.pop() as string;
      if (!result.has(current)) {
        result.add(current);
        const kids = childrenByParent[current] || [];
        kids.forEach((kid) => stack.push(kid));
      }
    }
    return result;
  };

  return Array.from(collectDescendants(nodeId));
}

export function normalizePermissionLevel(
  value: number | string | null | undefined
): number | undefined {
  if (value === null || value === undefined) return undefined;
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
  return Number.isFinite(parsed as number) ? (parsed as number) : undefined;
}

export function getHqPermissionLevel(
  permissionsOrTrainer:
    | Record<string, { level?: number | string }>
    | Trainer
    | undefined,
  key: string
): number | undefined {
  if (!permissionsOrTrainer) return undefined;
  let permissions: Record<string, { level?: number | string }> | undefined;
  if ('hqPermissions' in (permissionsOrTrainer as Trainer)) {
    permissions = (permissionsOrTrainer as Trainer).hqPermissions as any;
  } else {
    permissions = permissionsOrTrainer as Record<
      string,
      { level?: number | string }
    >;
  }
  if (!permissions) return undefined;
  const entry = permissions[key];
  if (!entry) return undefined;
  return normalizePermissionLevel(entry.level);
}

/**
 * Checks if a node is a descendant of (or the same as) another node
 * @param targetNodeId The node to check
 * @param ancestorNodeId The potential ancestor node
 * @param nodes Array of all nodes to traverse
 * @returns true if targetNode is a descendant of ancestorNode (or is the same node)
 */
export function isNodeDescendant(
  targetNodeId: Identifier | string | number,
  ancestorNodeId: Identifier | string | number,
  nodes: MinimalNode[]
): boolean {
  const target = String(targetNodeId);
  const ancestor = String(ancestorNodeId);

  // Same node is considered a descendant of itself
  if (target === ancestor) return true;

  // Build parent lookup for efficient traversal
  const parentLookup: Record<string, string> = {};
  nodes.forEach((node) => {
    if (node.parentId != null) {
      parentLookup[String(node.id)] = String(node.parentId);
    }
  });

  // Traverse up from target node looking for ancestor
  let currentId: string | undefined = target;
  while (currentId && currentId !== ancestor) {
    currentId = parentLookup[currentId];
  }

  return currentId === ancestor;
}

/**
 * Checks if a platform is accessible based on trainer's node assignment
 * @param platformId The platform to check
 * @param trainerNodeId The trainer's assigned node
 * @param platforms Array of all platforms
 * @param nodes Array of all nodes
 * @returns true if platform is linked to a node that's a descendant of trainer's node
 */
export function isPlatformDescendant(
  platformId: Identifier | string | number,
  trainerNodeId: Identifier | string | number,
  platforms: MinimalPlatform[],
  nodes: MinimalNode[]
): boolean {
  const platform = platforms.find((p) => String(p.id) === String(platformId));
  if (!platform || !platform.fbmPlatformNodeId) return false;

  return isNodeDescendant(platform.fbmPlatformNodeId, trainerNodeId, nodes);
}

/**
 * Gets all node IDs that are descendants of (or the same as) a given node
 * @param ancestorNodeId The ancestor node
 * @param nodes Array of all nodes
 * @returns Array of node IDs that are descendants of the ancestor node
 */
export function getAllDescendantNodeIds(
  ancestorNodeId: Identifier | string | number,
  nodes: MinimalNode[]
): string[] {
  const ancestor = String(ancestorNodeId);
  const descendants = new Set<string>([ancestor]); // Include the ancestor itself

  // Build children lookup for efficient traversal
  const childrenLookup: Record<string, string[]> = {};
  nodes.forEach((node) => {
    if (node.parentId != null) {
      const parentId = String(node.parentId);
      if (!childrenLookup[parentId]) childrenLookup[parentId] = [];
      childrenLookup[parentId].push(String(node.id));
    }
  });

  // Use stack-based traversal to collect all descendants
  const stack = [ancestor];
  while (stack.length > 0) {
    const currentId = stack.pop()!;
    const children = childrenLookup[currentId] || [];
    children.forEach((childId) => {
      if (!descendants.has(childId)) {
        descendants.add(childId);
        stack.push(childId);
      }
    });
  }

  return Array.from(descendants);
}

/**
 * Computes allowed IDs for HQ-restricted location filtering
 * @param trainer The trainer object with HQ assignment information
 * @param platforms Array of platforms
 * @param nodes Array of platform nodes
 * @returns Array of allowed IDs (platforms and nodes with ' node' suffix) or undefined if no restriction
 */
export function getHqAllowedLocationIds(
  trainer: Trainer | undefined,
  platforms: MinimalPlatform[],
  nodes: MinimalNode[]
): string[] | undefined {
  if (!trainer || !platforms || !nodes) return undefined;

  if (trainer.hqAssignmentType === 'platform' && trainer.hqAssignmentId) {
    return [String(trainer.hqAssignmentId)];
  }

  if (trainer.hqAssignmentType === 'platform_node') {
    const nodeIds = getHqAssignedDescendantNodeIds(trainer, nodes);
    const platformIds = getHqAssignedDescendantPlatformIds(
      trainer,
      platforms,
      nodes
    );
    return [...nodeIds.map((nid) => `${nid} node`), ...platformIds];
  }

  return undefined;
}
